import type {PropertyMap} from "../../common/utils/objects";
import {extendPrototype, type Patched} from "../../common/utils/extend";
import {GLOBAL} from "../../common/globals";
import type {Maybe, Nullable} from "./types";

export class Attribute {
    public constructor(public name: string, public value: string) {
    }
}

export interface Hideable {
    originalStyle: Maybe<string>;
}

extendPrototype(HTMLInputElement, {
    attachedFiles: function (this: HTMLInputElement): File[] {
        if (!this.files) {
            return [];
        }
        return Array.from(this.files);
    }
});

export type EventAttachable = {
    _events: PropertyMap<boolean>;
};

const valueFrom = (cssProperty: string): number => {
    return Number.parseInt(cssProperty) || 0;
};

const CONSIDERED_CLICKABLE_TAGS = new Set(["a", "button", "eop-background-link", "eop-page-overlay-link"]);
const IGNORED_CLICKABLE_TAGS = new Set(["header", "main", "footer"]);

extendPrototype(Element, {
    computedStyle: function (this: Element): CSSStyleDeclaration {
        return GLOBAL.window().getComputedStyle(this);
    },
    isFocused: function (this: Element): boolean {
        return this === GLOBAL.document().activeElement;
    },
    moveAttributes: function (this: Element, to: Element, predicate?: (attr: string) => boolean): void {
        const attributes = this.filteredAttributes(predicate);

        attributes.forEach(attribute => {
            this.removeAttribute(attribute.name);
            to.setAttribute(attribute.name, attribute.value);
        });
    },
    filteredAttributes: function (this: Element, predicate?: (attr: string) => boolean): Attribute[] {
        return Array.from(this.attributes)
            .filter(a => a.name !== "id")
            .filter(a => !predicate || predicate(a.name))
            .map(a => new Attribute(a.name, a.value));
    },
    closestThat: function (this: Element, predicate: (e: Element) => boolean): Nullable<Element> {
        if (predicate(this)) {
            return this;
        }
        return this.parentElement?.closestThat(predicate) ?? null;
    },
    hasAncestor: function (this: Element, ancestor: Element): boolean {
        return this !== ancestor
            && this.closestThat(e => e === ancestor) !== null;
    },
    hasDescendant: function (this: Element, descendant: Element): boolean {
        return descendant.hasAncestor(this);
    },
    readRawData: function <T>(this: Element, id: string): T {
        const scriptElement = this.querySelector<HTMLScriptElement>("script[data-id='" + id + "']");
        if (scriptElement?.text) {
            return JSON.parse(scriptElement.text) as T;
        }
        return {} as T;
    },
    resolveTemplateElement: function (this: Element, id: string): HTMLTemplateElement {
        return this.querySelector<HTMLTemplateElement>("template[data-id='" + id + "']")!;
    },
    isClickable: function (this: Element): boolean {
        if (this === GLOBAL.bodyElement()) {
            return false;
        }
        if (this === GLOBAL.htmlElement()) {
            return false;
        }
        const elementTag = this.tagName.toLowerCase();
        if (IGNORED_CLICKABLE_TAGS.has(elementTag)) {
            return false;
        }

        return CONSIDERED_CLICKABLE_TAGS.has(elementTag)
            || !!(this as any).onclick
            || (this as any)._events?.click
            || (this as any)._events?.submit
            || false;
    },
    // gets overridden for tests in testSetup.ts
    isVisible: function (this: Element): boolean {
        if (this.getClientRects().length === 0) {
            return false;
        }
        if (this instanceof HTMLElement && this.hidden) {
            return false;
        }
        const style = GLOBAL.window().getComputedStyle(this);
        return style.opacity !== "0"
            && style.display !== "none"
            && style.visibility !== "collapse"
            && style.visibility !== "hidden";
    },
    hide: function (this: Element): void {
        if (this instanceof HTMLElement || this instanceof SVGElement) {
            let originalStyle: Maybe<string> = this.style.display;
            if (originalStyle === "none") {
                originalStyle = undefined;
            }
            (this as any as Hideable).originalStyle = originalStyle;
            this.style.display = "none";
        }
    },
    show: function (this: Element): void {
        if (this instanceof HTMLElement || this instanceof SVGElement) {
            const originalStyle = (this as any as Hideable).originalStyle;
            if (originalStyle) {
                this.style.display = originalStyle;
                return;
            }

            if (this.style.display === "none") {
                this.style.removeProperty("display");
            }

            if (this.computedStyle().display === "none") {
                this.style.display = "block";
            }
        }
    },
    leftOffset: function (this: Element): number {
        return this.getBoundingClientRect()?.left + GLOBAL.window().scrollX;
    },
    rightOffset: function (this: Element): number {
        return this.getBoundingClientRect()?.right + GLOBAL.window().scrollX;
    },
    topOffset: function (this: Element): number {
        return this.getBoundingClientRect()?.top + GLOBAL.window().scrollY;
    },
    bottomOffset: function (this: Element): number {
        return this.getBoundingClientRect()?.bottom + GLOBAL.window().scrollY;
    },
    addEventListener: function <K extends keyof ElementEventMap>(type: K, listener: (this: Element, ev: ElementEventMap[K]) => any, options?: Maybe<boolean | AddEventListenerOptions>) {
        (this as any as EventAttachable)._events ??= {};
        (this as any as EventAttachable)._events[type] = true;
        (Element as any as Patched)._original.addEventListener.bind(this)(type, listener, options);
    },
    parent: function (this: Element): Element | null {
        return this.parentElement;
    },
    parents: function (this: Element): HTMLElement[] {
        const parents: HTMLElement[] = [];
        let nextParent = this.parentElement;
        while (nextParent) {
            parents.push(nextParent);
            nextParent = nextParent.parentElement;
        }
        return parents;
    },
    nextAll: function (this: Element): Element[] {
        const allChildren = Array.from(this.parentNode?.children ?? new HTMLCollection());
        const selfIndex = allChildren.indexOf(this);
        return allChildren.filter((item) => allChildren.indexOf(item) > selfIndex);
    },
    measuredOuterHeight: function (this: Element, options: SizeOptions = {}): number {
        const styles = GLOBAL.window().getComputedStyle(this);
        const border = valueFrom(styles.borderTopWidth) + valueFrom(styles.borderBottomWidth);
        let margin = 0;
        if (options.includeMargin === true) {
            margin = valueFrom(styles.marginTop) + valueFrom(styles.marginBottom);
        }
        return this.clientHeight + border + margin;
    },
    measuredOuterWidth: function (this: Element, options: SizeOptions = {}): number {
        const styles = GLOBAL.window().getComputedStyle(this);
        const border = valueFrom(styles.borderLeftWidth) + valueFrom(styles.borderRightWidth);
        let margin = 0;
        if (options.includeMargin === true) {
            margin = valueFrom(styles.marginLeft) + valueFrom(styles.marginRight);
        }
        return this.clientWidth + border + margin;
    }
});

declare global {

    interface HTMLInputElement {
        attachedFiles: () => File[];
    }

    interface Element {
        computedStyle: () => CSSStyleDeclaration;
        isFocused: () => boolean;
        moveAttributes: (to: Element, predicate?: (attr: string) => boolean) => void;
        filteredAttributes: (predicate?: (attr: string) => boolean) => Attribute[];
        closestThat: (predicate: (e: Element) => boolean) => Nullable<Element>;
        hasAncestor: (ancestor: Element) => boolean;
        hasDescendant: (descendant: Element) => boolean;
        readRawData: <T>(this: Element, id: string) => T;
        resolveTemplateElement: (this: Element, id: string) => HTMLTemplateElement;
        isClickable: () => boolean;
        isVisible: () => boolean;
        hide: () => void;
        show: () => void;
        leftOffset: () => number;
        rightOffset: () => number;
        topOffset: () => number;
        bottomOffset: () => number;
        parent: () => Element | null;
        parents: () => HTMLElement[];
        nextAll: () => Element[];
        measuredOuterHeight: (options?: SizeOptions) => number;
        measuredOuterWidth: (options?: SizeOptions) => number;
    }
}

type SizeOptions = {
    includeMargin?: boolean;
};